import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Clock } from "lucide-react";
import { securityService } from "@/services/security/securityService";
import { useAuthStore } from "@/stores/auth/auth-store";
import { useReAuthStore } from "@/stores/auth/reauth-store";

/**
 * Non-blocking inactivity warning modal.
 * Listens for `terramatrix:inactivity-warning` events dispatched by securityService
 * and gives the user a choice to continue or log out — without blocking the main
 * thread the way `confirm()` does.
 */
export const InactivityWarningModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const logout = useAuthStore((s) => s.logout);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isReAuthOpen = useReAuthStore((s) => s.isOpen);
  const navigate = useNavigate();

  const handleContinue = useCallback(() => {
    securityService.resetActivity();
    setIsOpen(false);
  }, []);

  const handleLogout = useCallback(async () => {
    setIsOpen(false);
    await logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  useEffect(() => {
    const onInactivityWarning = () => {
      // Only show the warning if the user is actually signed in
      const { isAuthenticated: authed } = useAuthStore.getState();
      const { isOpen: reAuthOpen } = useReAuthStore.getState();
      if (!authed || reAuthOpen) return;
      setIsOpen(true);
    };

    window.addEventListener(
      "terramatrix:inactivity-warning",
      onInactivityWarning,
    );
    return () => {
      window.removeEventListener(
        "terramatrix:inactivity-warning",
        onInactivityWarning,
      );
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || isReAuthOpen) {
      setIsOpen(false);
    }
  }, [isAuthenticated, isReAuthOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleContinue(); }}>
      <DialogContent variant="light" className="sm:max-w-[440px] p-0 overflow-hidden">
        {/* Header with icon badge */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Clock className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <DialogTitle className="text-lg font-semibold text-foreground">
                Are you still there?
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                You have been inactive for a while. Would you like to continue your session?
              </DialogDescription>
            </div>
          </div>
        </div>

        {/* Footer with actions */}
        <div className="border-t border-border bg-muted/30 px-6 py-4 space-y-3">
          <Button
            onClick={handleContinue}
            className="w-full h-10 font-medium transition-all duration-200 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Continue Session
          </Button>
          <button
            type="button"
            onClick={handleLogout}
            className="w-full text-center text-sm text-muted-foreground hover:text-primary hover:underline focus:outline-none focus:underline transition-colors"
          >
            Log Out
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InactivityWarningModal;
